import { userPlaylistId as buildProviderPlaylistId } from '../ids';
import {
  CATALOG_SCHEMA_VERSION,
  AVAILABILITY_STATES,
  DISPLAY_NAME_SOURCES,
  PLAYLIST_KINDS,
  SYNC_STATUSES,
  buildUserPlaylist,
  normalizeUserPlaylistForRead,
} from '../schema';

function requireDocument(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new TypeError('catalog document must be an object');
  }
  return raw;
}

function legacyCount(value) {
  return Number.isSafeInteger(value) && value >= 0 ? value : 0;
}

function legacyText(value, fallback = '') {
  return typeof value === 'string' ? value : fallback;
}

/**
 * Adapts the actual pre-Phase-4 importedPlaylists shape:
 * { playlistId, title, thumbnail, itemCount, source, importedAt, updatedAt }.
 */
export function migrateLegacyPlaylistV0ToV1(raw) {
  const value = requireDocument(raw);
  const providerPlaylistId = legacyText(value.playlistId);
  if (!providerPlaylistId) {
    throw new TypeError('legacy playlistId must be a nonempty string');
  }

  // importedPlaylists.js is a YouTube-only feature and writes source:"youtube".
  // Preserve an unexpected source honestly rather than assigning it a made-up
  // Rhythmé namespace; readers still support bare legacy IDs.
  const provider = legacyText(value.source, 'youtube');
  const migratedUserPlaylistId =
    provider === 'youtube'
      ? buildProviderPlaylistId('youtube', providerPlaylistId)
      : providerPlaylistId;
  const itemCount = legacyCount(value.itemCount);

  return buildUserPlaylist({
    userPlaylistId: migratedUserPlaylistId,
    kind: PLAYLIST_KINDS.PROVIDER_MIRROR,
    provider,
    providerPlaylistId,
    providerOwnerChannelId: null,
    providerSnapshot: {
      title: legacyText(value.title),
      description: null,
      thumbnailUrl:
        typeof value.thumbnail === 'string' ? value.thumbnail : null,
      itemCount,
      privacyStatus: null,
      etag: null,
      observedAt: null,
    },
    displayName: legacyText(value.title),
    displayNameSource: DISPLAY_NAME_SOURCES.PROVIDER,
    knownItemCount: itemCount,
    loadedItemCount: 0,
    playableItemCount: 0,
    availability: AVAILABILITY_STATES.UNKNOWN,
    sync: {
      status: SYNC_STATUSES.IDLE,
      syncRunSeq: 0,
    },
    lease: null,
    lifecycle: {
      obtainedVia: 'oauth_user_grant',
      lastFetchedAt: null,
      lastRefreshedAt: null,
      refreshDueAt: null,
      expiresAt: null,
    },
    importedAt: value.importedAt ?? null,
    createdAt: value.importedAt ?? null,
    updatedAt: value.updatedAt ?? value.importedAt ?? null,
  });
}

export function migratePlaylistOnRead(
  raw,
  targetVersion = CATALOG_SCHEMA_VERSION,
) {
  const value = requireDocument(raw);
  if (!Number.isSafeInteger(targetVersion) || targetVersion < 0) {
    throw new TypeError('targetVersion must be a nonnegative safe integer');
  }
  if (targetVersion > CATALOG_SCHEMA_VERSION) {
    throw new RangeError(
      `No migration path exists for catalog schema version ${targetVersion}`,
    );
  }

  const sourceVersion = value.catalogSchemaVersion ?? 0;
  if (!Number.isSafeInteger(sourceVersion) || sourceVersion < 0) {
    throw new TypeError(
      'catalogSchemaVersion must be a nonnegative safe integer',
    );
  }

  if (sourceVersion === 0 && targetVersion >= 1) {
    return normalizeUserPlaylistForRead(
      migrateLegacyPlaylistV0ToV1(value),
    );
  }

  // Never downgrade or reject a future document. Known enum fields are read
  // through conservative fallbacks while the original schema version remains.
  return normalizeUserPlaylistForRead({ ...value });
}

// The only version-0 catalog document today is UserPlaylist. Keep the
// architecture's generic name while the migration surface has one real type.
export const migrateOnRead = migratePlaylistOnRead;
